<template>
  <div class="bg-white min-h-screen">
    <!-- Hero Banner Section -->
    <section class="bg-gradient-to-br from-[#CB2530] to-[#B41923] relative overflow-hidden pt-24 lg:pt-32 pb-12 lg:pb-16 min-h-[320px] lg:min-h-[400px] flex items-center">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 w-full">
        <h1 class="text-4xl md:text-5xl font-medium text-white mb-6 tracking-tight leading-tight">{{ partnerConfigs.partner_banner_title || $t('common.partners.title') }}</h1>
        <p class="text-xl text-white/85 max-w-3xl mx-auto leading-relaxed whitespace-pre-line">
          {{ partnerConfigs.partner_banner_desc }}
        </p>
      </div>
    </section>

    <section class="py-20 bg-white border-b border-slate-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="(stat, index) in stats" :key="index" 
               class="text-center p-6 bg-gradient-to-br from-[#FDF3F4] to-[#FBEEEF] rounded-xl border border-[#F2D6D8]"
          >
            <div class="text-[#C6202B] mb-4 flex items-center justify-center">
              <img v-if="isExternalUrl(stat.iconName)" :src="processImageUrl(stat.iconName)" class="w-10 h-10 object-contain" />
              <i v-else :class="getIconClass(stat.iconName)" class="text-4xl"></i>
            </div>
            <div class="text-4xl text-slate-900 mb-2 font-medium">{{ stat.value }}</div>
            <div class="text-slate-600">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Application Fields Section -->
    <section class="py-20 bg-slate-50">
      <div class="container-custom">
        <div class="text-center mb-16">
          <h2 class="text-3xl text-slate-900 mb-4 tracking-tight font-medium">{{ $t('common.partners.applicationTitle') }}</h2>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
          <div v-for="(field, index) in applicationFields" :key="index" 
               class="group cursor-pointer bg-white rounded-xl overflow-hidden transition-all duration-500 hover:shadow-xl border border-slate-100 shadow-md">
            <!-- Top part: Image -->
            <div class="aspect-[4/3] overflow-hidden">
              <img :src="processImageUrl(field.image)" :alt="field.title" class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
            </div>
            <!-- Bottom part: Text area -->
            <div class="p-3 text-center space-y-1 transition-colors duration-500 group-hover:bg-slate-50">
              <p class="text-gray-900 text-sm font-medium">{{ field.title }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Partners Logo Grid Section -->
    <section class="py-20 bg-white">
      <div class="container-custom">
        <div class="text-center mb-12">
          <h2 class="text-3xl text-slate-900 mb-4 tracking-tight font-medium">{{ $t('common.partners.partnerTitle') }}</h2>
          <p class="text-slate-600 text-base">{{ $t('common.partners.partnerSubtitle') }}</p>
        </div>
        <div class="mt-12">
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            <div v-for="(partner, index) in partners" :key="index" 
                 class="aspect-[4/3] bg-white border border-slate-200 rounded-lg flex items-center justify-center p-4 hover:border-[#C6202B]/40 hover:shadow-lg transition-all group cursor-pointer">
              <div class="text-center w-full flex flex-col items-center justify-center">
                <div v-if="partner.partnerLogo" class="w-full h-32 lg:h-40 flex items-center justify-center mb-4">
                  <img :src="processImageUrl(partner.partnerLogo)" :alt="partner.partnerName" class="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform" style="image-rendering: -webkit-optimize-contrast;" />
                </div>
                <div v-else class="text-lg lg:text-xl font-bold text-slate-200 group-hover:text-[#C6202B]/30 transition-colors uppercase tracking-widest text-center mb-4">
                  {{ partner.partnerName }}
                </div>
                <div class="text-sm font-medium text-slate-700 group-hover:text-[#C6202B] transition-colors tracking-tight text-center line-clamp-1">{{ partner.partnerName }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Quality Commitment Section -->
    <section class="py-20 bg-gradient-to-br from-[#150A0C] to-[#3A1116] relative overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <!-- Left Content -->
          <div>
            <h2 class="text-3xl text-white mb-6 tracking-tight font-medium">{{ partnerConfigs.partner_commit_title }}</h2>
            <div 
              :class="[$i18n.locale === 'en' ? 'text-left hyphens-auto' : 'text-justify', 'space-y-4 text-slate-300 leading-relaxed text-base font-normal whitespace-pre-line']"
              :lang="$i18n.locale"
            >
              {{ partnerConfigs.partner_commit_desc }}
            </div>
          </div>
          <!-- Right Grid -->
          <div class="grid grid-cols-2 gap-6">
            <div v-for="(commit, index) in commitments" :key="index" 
                 class="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-500 group">
              <div class="text-3xl text-white mb-2 tracking-tight">{{ commit.title }}</div>
              <p 
                :class="[$i18n.locale === 'en' ? 'text-left hyphens-auto' : 'text-justify', 'text-slate-300 text-sm leading-relaxed']"
                :lang="$i18n.locale"
              >
                {{ commit.desc }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getPublicPartners, getPublicPartnerStats, getPublicPartnerConfigs, getPublicPartnerApplications } from '@/api/partner'

export default {
  name: 'PartnersView',
  props: {
    preloadedData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      partnerConfigs: {
        partner_banner_title: '',
        partner_banner_desc: '',
        partner_commit_title: '',
        partner_commit_desc: ''
      },
      stats: [],
      applicationFields: [],
      partners: []
    }
  },
  computed: {
    commitments() {
      // 增加防御性编程，确保 $t 可用且有值
      const zeroDefect = this.$t('common.partners.zeroDefect') || '';
      const qualityTarget = this.$t('common.partners.qualityTarget') || '';
      // ... 保持原有逻辑但增加空值处理
      return [
        { title: this.$t('common.partners.zeroDefect'), desc: this.$t('common.partners.qualityTarget') },
        { title: this.$t('common.partners.timeliness'), desc: this.$t('common.partners.techSupport') },
        { title: this.$t('common.partners.customization'), desc: this.$t('common.partners.solutions') },
        { title: this.$t('common.partners.fullCoverage'), desc: this.$t('common.partners.serviceNetwork') }
      ]
    }
  },
  watch: {
    preloadedData: {
      handler(newVal) {
        if (newVal) {
          this.applyPreloadedData(newVal);
        }
      },
      immediate: true
    }
  },
  created() {
    this.fetchPartnerData()
    this.$root.$on('language-changed', this.fetchPartnerData)
  },
  beforeDestroy() {
    this.$root.$off('language-changed', this.fetchPartnerData)
  },
  methods: {
    applyPreloadedData(data) {
      if (data.configs) {
        data.configs.forEach(item => {
          if (this.partnerConfigs.hasOwnProperty(item.configKey)) {
            this.partnerConfigs[item.configKey] = item.configValue
          }
        })
      }
      if (data.stats && data.stats.length > 0) this.stats = data.stats
      if (data.list && data.list.length > 0) this.partners = data.list
      if (data.applications && data.applications.length > 0) this.applicationFields = data.applications
    },
    async fetchPartnerData() {
      try {
        const [configRes, statRes, partnerRes, applicationRes] = await Promise.all([
          getPublicPartnerConfigs(),
          getPublicPartnerStats(),
          getPublicPartners(),
          getPublicPartnerApplications()
        ])

        const freshData = {
          configs: configRes.data,
          stats: statRes.data,
          list: partnerRes.data,
          applications: applicationRes.data
        };
        this.applyPreloadedData(freshData);
      } catch (error) {
        console.error('Failed to fetch partner data:', error)
      }
    },
    processImageUrl(url) {
      if (!url) return ''
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url
      const baseUrl = process.env.VUE_APP_BASE_API || 'http://localhost:8080'
      return baseUrl + url
    },
    isExternalUrl(url) {
      return url && (url.startsWith('/') || url.startsWith('http'))
    },
    getIconClass(name) {
      const iconMap = {
        Users: 'el-icon-user-solid',
        Globe: 'el-icon-position',
        CheckCircle: 'el-icon-circle-check',
        TrendingUp: 'el-icon-data-line',
        Zap: 'el-icon-bangzhu'
      }
      return iconMap[name] || 'el-icon-star-on'
    }
  }
}
</script>

<style scoped>
.container-custom {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
</style>
